import { useCallback, useEffect, useMemo, useState } from 'react'
import { clearToken, getState, getToken, onUnauthorized } from './api/client'
import DebugConsole from './components/DebugConsole'
import EditSheet from './components/EditSheet'
import Capture from './views/Capture'
import Day from './views/Day'
import Coach from './views/Coach'
import Lists from './views/Lists'
import Login from './views/Login'
import type { Item, State } from './types'

type Tab = 'day' | 'coach' | 'lists' | 'cap'

export default function App() {
  const [token, setTokenState] = useState<string | null>(getToken())
  const [tab, setTab] = useState<Tab>('day')
  const [state, setState] = useState<State | null>(null)
  const [editing, setEditing] = useState<Item | null>(null)
  // Re-clarifier un item précis (ex. le passer en projet) : remonte le coach à neuf.
  const [startItemId, setStartItemId] = useState<string | null>(null)
  const [coachRun, setCoachRun] = useState(0)
  const [dayRun, setDayRun] = useState(0)

  useEffect(() => onUnauthorized(() => setTokenState(null)), [])

  const projectTitles = useMemo(() => {
    const map: Record<string, string> = {}
    for (const p of state?.projects ?? []) map[p.id] = p.title
    return map
  }, [state])

  const refresh = useCallback(async () => {
    try {
      setState(await getState())
    } catch {
      /* 401 déjà traité par le client */
    }
  }, [])

  // Les listes et l'inbox se rechargent à chaque fois qu'on revient dessus.
  useEffect(() => {
    if (token && (tab === 'lists' || tab === 'cap' || tab === 'day')) void refresh()
  }, [token, tab, refresh])

  // Revenir sur l'onglet Jour doit rejouer GET /api/day : une tâche planifiée
  // depuis les listes ne serait pas visible sinon.
  useEffect(() => {
    if (tab === 'day') setDayRun((r) => r + 1)
  }, [tab])

  const reclarify = useCallback((id: string) => {
    setEditing(null)
    setStartItemId(id)
    setCoachRun((r) => r + 1)
    setTab('coach')
  }, [])

  const logout = () => {
    clearToken()
    setTokenState(null)
  }

  if (!token) return <Login onLoggedIn={(t) => { setTokenState(t); setTab('day') }} />

  return (
    <>
      <div className="app">
        <header>
          <div className="logo">
            Klary<b>go</b>
          </div>
          <div className="sub">coach gtd</div>
          <button className="logout" onClick={logout} title="Déconnexion">
            ⏻
          </button>
        </header>
        <nav>
          <button className={tab === 'day' ? 'on' : ''} onClick={() => setTab('day')}>
            Jour
          </button>
          <button className={tab === 'coach' ? 'on' : ''} onClick={() => setTab('coach')}>
            Clarifier
          </button>
          <button className={tab === 'lists' ? 'on' : ''} onClick={() => setTab('lists')}>
            Listes
          </button>
          <button className={tab === 'cap' ? 'on' : ''} onClick={() => setTab('cap')}>
            Capturer
          </button>
        </nav>
        <main>
          {/* Les trois vues restent montées : la clarification en cours survit à un
              aller-retour vers les listes, comme dans la version d'origine. */}
          <div style={{ display: tab === 'day' ? 'block' : 'none' }}>
            <Day key={dayRun} projects={projectTitles} onEdit={setEditing} onChanged={refresh} />
          </div>
          <div style={{ display: tab === 'coach' ? 'block' : 'none' }}>
            <Coach key={coachRun} startItemId={startItemId} onStartConsumed={() => setStartItemId(null)} />
          </div>
          <div style={{ display: tab === 'lists' ? 'block' : 'none' }}>
            <Lists state={state} onEdit={setEditing} onReclarify={reclarify} onChanged={refresh} />
          </div>
          <div style={{ display: tab === 'cap' ? 'block' : 'none' }}>
            <Capture state={state} onEdit={setEditing} onReclarify={reclarify} onChanged={refresh} />
          </div>
        </main>
        {editing && (
          <EditSheet
            item={editing}
            projects={state?.projects ?? []}
            onClose={() => setEditing(null)}
            onChanged={refresh}
            onReclarify={reclarify}
          />
        )}
      </div>
      <DebugConsole />
    </>
  )
}
